"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  MotionValue,
  useAnimationFrame,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { PortableText } from "next-sanity";
import { PortableTextBlock } from "sanity";
import { stegaClean } from "@sanity/client/stega";
import Link from "next/link";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";
import { GROQImage, ITraderStory } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { resolveI18nText, resolveI18nRichtext } from "@/sanity/globals/I18n";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageHomeLivePayoutItem, PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import GradualBlur from "@/components/global/GradualBlur";
import { useIsMobile } from "@/hooks/useIsMobile";

// Home-only Traders Stories + Live Payouts with more contrast: light story card, neutral payout cards.
// Design: Figma "IQ Capital | New UI 2026" node 10551-1695 (desktop only; mobile takes the same palette).
// The shared TradersStoriesSection / LivePayoutsSection keep the previous look on the other pages.
// Sizes are Figma px / 8 → rem.

const GAP_REM = 2;
const PEEK_FACTOR = 0.22;

const REPEATS = 4;
const BASE_PX_PER_FRAME = 1.5;
const GAP_PX = 24;

interface TradersStoriesData {
  chip?: string;
  title?: PortableTextBlock[];
  items?: ITraderStory[];
}

// ─── Icons ───────────────────────────────────────────────────────────────────

/** Filled arrow used by the mobile "Read the full story" link. */
function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Thin chevron for the mobile pager buttons. */
function ChevronIcon({ direction, className }: { direction: "left" | "right"; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 17.4479 17.4479"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d={
          direction === "left"
            ? "M10.9054 3.25977L5.81641 8.34874L10.9054 13.4377"
            : "M6.54297 3.25977L11.6319 8.34874L6.54297 13.4377"
        }
        stroke="currentColor"
        strokeWidth="0.727"
      />
    </svg>
  );
}

function IQLogo() {
  return (
    <svg
      viewBox="0 0 31.0799 25.9511"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[3.25rem] w-[3.875rem] shrink-0"
      aria-hidden="true">
      <path
        d="M0 24.2671V0.787903H5.73838V24.2671H0Z"
        fill="white"
        fillOpacity="0.26"
      />
      <path
        d="M25.5823 25.9511L23.8006 23.7691C23.319 24.0106 22.7091 24.1958 21.9868 24.3327C21.2645 24.4615 20.4619 24.534 19.5791 24.534C17.2516 24.534 15.2131 24.0187 13.4795 22.9961C11.746 21.9735 10.4057 20.5322 9.45864 18.6803C8.5116 16.8284 8.03809 14.7027 8.03809 12.2952C8.03809 9.88768 8.4795 7.86667 9.3543 6.01474C10.2291 4.16281 11.5373 2.69737 13.2628 1.61842C14.9884 0.539475 17.0911 0 19.5791 0C22.067 0 24.1617 0.539475 25.8712 1.61842C27.5887 2.69737 28.8809 4.16281 29.7637 6.01474C30.6385 7.86667 31.0799 9.96015 31.0799 12.2952C31.0799 14.4772 30.7187 16.3694 29.9884 17.9637C29.2661 19.566 28.2388 20.9107 26.9226 22.0138L30.0847 25.9511H25.5662H25.5823ZM19.5871 19.8317C19.8279 19.8317 20.0526 19.8156 20.2452 19.7834C20.4458 19.7512 20.6304 19.7029 20.807 19.6304L17.6128 15.7253H22.1312L23.5839 17.6416C24.0895 17.0941 24.4828 16.3936 24.7717 15.5401C25.0606 14.6946 25.1971 13.6076 25.1971 12.2791C25.1971 10.9505 25.0044 9.68639 24.6192 8.54302C24.234 7.39966 23.632 6.46564 22.8054 5.74903C21.9787 5.03241 20.9113 4.67813 19.5951 4.67813C18.2789 4.67813 17.1713 5.04046 16.3447 5.74903C15.518 6.46564 14.9161 7.39966 14.5309 8.54302C14.1457 9.68639 13.953 10.9344 13.953 12.2791C13.953 13.6237 14.1537 14.8396 14.5469 15.9829C14.9402 17.1263 15.5501 18.0523 16.3768 18.7608C17.2034 19.4694 18.2709 19.8156 19.5871 19.8156V19.8317Z"
        fill="white"
        fillOpacity="0.26"
      />
    </svg>
  );
}

// ─── Story content (shared by both card layouts) ─────────────────────────────

function useStoryContent(story: ITraderStory, language?: string) {
  const title = resolveI18nRichtext(story.title, story.titleI18n, language);
  const traderRole = resolveI18nText(story.traderRole, story.traderRoleI18n, language);
  const question = resolveI18nText(story.question, story.questionI18n, language);
  const answer = resolveI18nText(story.answer, story.answerI18n, language);
  const watchInterviewLabel = resolveI18nText(story.watchInterviewLabel, story.watchInterviewLabelI18n, language);

  const link = story.watchInterviewLink ? resolvePageLink(story.watchInterviewLink, language) : null;
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const traderImage = (language && story.traderImageI18n?.[language as "de" | "br"]) || story.traderImage;

  return {
    title,
    traderRole,
    question,
    answer,
    ctaLabel: watchInterviewLabel || (story.hasVideo ? "Watch interview" : "Read Full Story"),
    finalLink,
    linkTarget: story.watchInterviewLink?.openInNewTab ? "_blank" : undefined,
    linkRel: story.watchInterviewLink?.openInNewTab ? "noopener noreferrer" : undefined,
    traderImage,
  };
}

type StoryContent = ReturnType<typeof useStoryContent>;

function StoryFigure({
  story,
  content,
  gradient,
  className,
}: {
  story: ITraderStory;
  content: StoryContent;
  gradient?: boolean;
  className?: string;
}) {
  const inner = (
    <>
      {content.traderImage && (
        <GroqImage
          src={content.traderImage as GROQImage}
          alt={story.traderName ?? ""}
          className="!absolute !inset-0 !h-full !w-full !object-cover"
        />
      )}
      {gradient && (
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,18,13,0)_45%,rgba(3,18,13,0.8)_100%)]" />
      )}
      {story.hasVideo && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Image
            src="/global/video-play-btn.png"
            alt=""
            width={80}
            height={80}
            className="h-[10rem] w-[10rem]"
          />
        </div>
      )}
    </>
  );

  return (
    <div className={cn("relative w-full shrink-0 overflow-hidden", className)}>
      {content.finalLink ? (
        <Link
          href={content.finalLink}
          target={content.linkTarget}
          rel={content.linkRel}
          className="absolute inset-0">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </div>
  );
}

// ─── Desktop story card ──────────────────────────────────────────────────────

const StoryCardDesktop = memo(function StoryCardDesktop({ story, language }: { story: ITraderStory; language?: string }) {
  const content = useStoryContent(story, language);
  const stats = story.stats ?? [];
  // Figma headlines this card with the trader's quote, one sentence per line, so it reads the answer without
  // its quote marks instead of the shared CMS title (whose dark highlight is built for the dark cards on other pages).
  const headline =
    content.answer &&
    stegaClean(content.answer)
      .trim()
      .replace(/^["„“”]+|["„“”]+$/g, "")
      .replace(/\.\s+/g, ".\n");

  return (
    <div className="flex w-full flex-col border-[0.1rem] border-white/[0.09] bg-[#f4f5f5] pt-[5rem]">
      <div className="flex items-center gap-[7.5rem] overflow-clip px-[4rem] pt-[0.5rem] pb-[4rem]">
        {/* Left — figure + name/role */}
        <div className="flex w-[66.125rem] shrink-0 flex-col gap-[2.5rem]">
          <StoryFigure
            story={story}
            content={content}
            gradient
            className="h-[39.54rem] border-[0.1rem] border-white/[0.14] bg-[#07261b]"
          />
          <div className="flex flex-col gap-[0.75rem]">
            {story.traderName && (
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.417] font-semibold tracking-[-0.021rem] text-black">
                {story.traderName}
              </p>
            )}
            {content.traderRole && (
              <p className="mb-0 font-geist text-[1.75rem] leading-[1.179] text-black uppercase">{content.traderRole}</p>
            )}
          </div>
        </div>

        {/* Right — headline, quote, description, CTA */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-[2.875rem]">
          {headline && (
            <div className="richtext richtext-start w-full prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[4.5rem] prose-headings:leading-[1.19] prose-headings:font-medium prose-headings:tracking-[-0.375rem] prose-headings:text-black">
              <h3 className="whitespace-pre-line">{headline}</h3>
            </div>
          )}
          <div className="h-px w-[64.125rem] max-w-full shrink-0 bg-black/10" />
          {content.answer && (
            <div className="border-l-[0.25rem] border-[#65b495] px-[1.25rem]">
              <p className="mb-0 max-w-[59.25rem] font-geist text-[2.25rem] leading-[1.4] tracking-[-0.1125rem] text-black">
                {content.answer}
              </p>
            </div>
          )}
          {content.question && (
            <p className="mb-0 max-w-[60.75rem] font-geist text-[2.25rem] leading-[1.34] tracking-[-0.0225rem] text-black/70">
              {content.question}
            </p>
          )}
          {content.finalLink && (
            <Link
              href={content.finalLink}
              target={content.linkTarget}
              rel={content.linkRel}
              className="flex items-center bg-black px-[2.5rem] py-[1.25rem] font-geist text-[2.25rem] leading-[1.5] font-semibold tracking-[-0.1125rem] whitespace-nowrap text-white transition-opacity hover:opacity-80">
              {content.ctaLabel}
            </Link>
          )}
        </div>
      </div>

      {/* Stats strip */}
      {stats.length > 0 && (
        <div className="grid auto-cols-fr grid-flow-col border-t-[0.1rem] border-[#1a1a1a]/[0.18] bg-[#f4f5f5]">
          {stats.map((stat, i) => (
            <div
              key={stat._key ?? i}
              className={cn(
                "flex min-w-0 flex-col px-[6rem] py-[3rem]",
                i > 0 && "border-l-[0.1rem] border-[#1a1a1a]/[0.18]",
              )}>
              <p className="mb-0 font-geist text-[3.25rem] leading-[1.1] font-medium tracking-[-0.0325rem] whitespace-nowrap text-black">
                {stat.value}
              </p>
              <p className="mb-0 pt-[0.75rem] font-geist text-[1.625rem] leading-[1.5] text-black/60">
                {resolveI18nText(stat.label, stat.labelI18n, language)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ─── Mobile story card ───────────────────────────────────────────────────────

const StoryCardMobile = memo(function StoryCardMobile({ story, language }: { story: ITraderStory; language?: string }) {
  const content = useStoryContent(story, language);

  return (
    <div className="flex h-full w-full flex-col rounded-[0.28rem] bg-[#f4f5f5] px-[2rem] py-[4rem] shadow-[0_4.75rem_14.0625rem_rgba(0,54,40,0.23)]">
      {content.title && (
        <div className="richtext richtext-start w-full px-[2rem] pt-[2rem] pb-[2.5rem] prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[3.5rem] prose-headings:leading-[1.455] prose-headings:font-normal prose-headings:tracking-[-0.168rem] prose-headings:text-black">
          <PortableText
            value={content.title}
            components={richTextComponent}
          />
        </div>
      )}

      <StoryFigure
        story={story}
        content={content}
        className="h-[26.293rem] rounded-[0.28rem]"
      />

      <div className="flex flex-col gap-[0.56rem] pt-[2.8rem] pb-[2rem] pl-[2rem]">
        {story.traderName && (
          <p className="mb-0 font-geist text-[2.8rem] leading-none font-medium tracking-[-0.14rem] text-black">
            {story.traderName}
          </p>
        )}
        {content.traderRole && (
          <p className="mb-0 font-geist-mono text-[1.96rem] leading-[1.33] font-semibold text-black/[0.43] uppercase">
            {content.traderRole}
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[2.125rem] p-[2rem]">
        <div className="h-px w-[calc(100%+2rem)] shrink-0 bg-black/10" />
        <div className="flex flex-col gap-[4.2rem]">
          {content.question && (
            <p className="mb-0 font-geist text-[2rem] leading-[1.567] font-medium tracking-[-0.112rem] text-black/60">
              {content.question}
            </p>
          )}
          {content.answer && (
            <div className="border-l-[0.25rem] border-[#65b495] pl-[1.96rem]">
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.567] font-medium tracking-[-0.126rem] text-black">
                {content.answer}
              </p>
            </div>
          )}
        </div>
        {content.finalLink && (
          <Link
            href={content.finalLink}
            target={content.linkTarget}
            rel={content.linkRel}
            className="mt-auto flex w-fit items-center gap-[1.68rem] font-geist text-[2.24rem] leading-[1.5] font-semibold tracking-[-0.112rem] text-black transition-opacity hover:opacity-70">
            {content.ctaLabel}
            <ArrowIcon className="h-[1.4rem] w-[1.783rem] shrink-0" />
          </Link>
        )}
      </div>
    </div>
  );
});

// ─── Progress indicator (isolated to avoid re-rendering card stack) ──────────

function ProgressIndicator({ count, progress }: { count: number; progress: MotionValue<number> }) {
  const [activeIndex, setActiveIndex] = useState(0);
  useMotionValueEvent(progress, "change", (v) => {
    const next = Math.min(count - 1, Math.round(v));
    setActiveIndex((prev) => (prev === next ? prev : next));
  });
  if (count <= 1) return null;
  return (
    <div className="flex w-[4rem] shrink-0 flex-col gap-[1.75rem]">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={cn(
            "h-[0.375rem] rounded-full transition-[width,background-color] duration-300",
            i === activeIndex ? "w-[4rem] bg-[#65b495]" : "w-[3rem] bg-white/20",
          )}
        />
      ))}
    </div>
  );
}

// ─── Desktop animated card ────────────────────────────────────────────────────

function buildPositions(cardHeightsPx: number[], gapPx: number): number[] {
  const positions: number[] = [];
  let cumY = 0;
  for (let i = 0; i < cardHeightsPx.length; i++) {
    positions[i] = cumY;
    cumY += (cardHeightsPx[i] ?? 0) + gapPx;
  }
  return positions;
}

function useCardTransforms(index: number, progress: MotionValue<number>, positionsRef: { current: number[] }) {
  const y = useTransform(progress, (p) => {
    const positions = positionsRef.current;
    if (!positions.length) return index === 0 ? 0 : 9999;
    const floorP = Math.max(0, Math.min(Math.floor(p), positions.length - 1));
    const fracP = p - floorP;
    const from = positions[floorP] ?? 0;
    const to = positions[Math.min(floorP + 1, positions.length - 1)] ?? from;
    const scrollPx = from + fracP * (to - from);
    return (positions[index] ?? 0) - scrollPx;
  });

  const opacity = useTransform(progress, (p) => {
    const r = index - p;
    if (r < 0) return 1;
    if (r <= 1) return 1 - r * 0.2;
    if (r <= 1.3) return ((1.3 - r) / 0.3) * 0.8;
    return 0;
  });

  const scale = useTransform(progress, (p) => {
    const r = index - p;
    if (r <= 0) return 1;
    return 1 - Math.min(r, 1) * 0.025;
  });

  return { y, opacity, scale };
}

const StoryCard = memo(function StoryCard({
  story,
  index,
  total,
  progress,
  positionsRef,
  measureRef,
  language,
}: {
  story: ITraderStory;
  index: number;
  total: number;
  progress: MotionValue<number>;
  positionsRef: { current: number[] };
  measureRef: (el: HTMLElement | null) => void;
  language?: string;
}) {
  const { y, opacity, scale } = useCardTransforms(index, progress, positionsRef);
  const [isActive, setIsActive] = useState(index === 0);
  useMotionValueEvent(progress, "change", (v) => {
    const active = Math.round(v) === index;
    setIsActive((prev) => (prev === active ? prev : active));
  });

  return (
    <motion.div
      ref={measureRef}
      style={{
        y,
        scale,
        opacity,
        zIndex: total - index,
        willChange: "transform, opacity",
      }}
      className={cn("absolute inset-x-0 top-0 flex", !isActive && "pointer-events-none")}>
      <StoryCardDesktop
        story={story}
        language={language}
      />
    </motion.div>
  );
});

// ─── Desktop stories (own component so useScroll reinitialises on remount) ───

function DesktopTradersStories({
  data,
  items,
  language,
}: {
  data: TradersStoriesData;
  items: ITraderStory[];
  language?: string;
}) {
  const count = items.length;
  const sectionRef = useRef<HTMLElement>(null);
  const cardElsRef = useRef<(HTMLElement | null)[]>([]);
  const cardHeightsPxRef = useRef<number[]>([]);
  const gapPxRef = useRef(GAP_REM * 8);
  const positionsRef = useRef<number[]>([]);
  const [containerH, setContainerH] = useState<string>("91rem");

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
    layoutEffect: false,
  });

  const rawProgress = useTransform(scrollYProgress, [0, 1], [0, Math.max(count - 1, 0)]);

  const smoothProgress = useSpring(rawProgress, {
    stiffness: 100,
    damping: 26,
    mass: 0.5,
    restDelta: 0.001,
    restSpeed: 0.001,
  });

  useEffect(() => {
    const recalculate = () => {
      const fs = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
      gapPxRef.current = GAP_REM * fs;
      const heights = cardElsRef.current.map((el) => el?.offsetHeight ?? 0);
      if (!heights.some((h) => h > 0)) return;
      cardHeightsPxRef.current = heights;
      positionsRef.current = buildPositions(heights, gapPxRef.current);
      const maxH = Math.max(...heights);
      setContainerH(`${maxH + gapPxRef.current + maxH * PEEK_FACTOR}px`);
    };
    recalculate();
    const ro = new ResizeObserver(recalculate);
    cardElsRef.current.forEach((el) => {
      if (el) ro.observe(el);
    });
    window.addEventListener("resize", recalculate);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalculate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const measureRefs = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => (el: HTMLElement | null) => {
        cardElsRef.current[i] = el;
      }),
    [count],
  );

  const sectionHeightRem = count <= 1 ? 100 : 100 + (count - 1) * 100;

  return (
    <div className="relative w-full bg-[#001b14]">
      <section
        ref={sectionRef}
        className="relative w-full"
        style={{ minHeight: `${sectionHeightRem}rem` }}>
        <div className="sticky top-0 flex h-[135rem] flex-col gap-[8rem] overflow-clip bg-[#001b14]">
          {/* Header */}
          <div className="mx-auto flex w-[min(145rem,calc(100%-8rem))] flex-col items-center gap-[2rem] pt-[12rem] text-center">
            {data.chip && (
              <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-white/70 italic">{data.chip}</p>
            )}
            {data.title && (
              <div className="richtext [&_p]:hidden prose-em:font-blacker prose-em:text-[#d6eee7] prose-headings:text-[6rem] prose-headings:leading-[1.15] prose-headings:font-medium prose-headings:tracking-[-0.3rem] prose-headings:text-white">
                <PortableText
                  value={data.title}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {/* Cards + progress indicator — 160rem row: 4rem indicator + 4rem gap + 152rem card (Figma 1214px) */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative flex w-[min(160rem,calc(100%-8rem))] items-center gap-[4rem]">
              <ProgressIndicator
                count={items.length}
                progress={smoothProgress}
              />
              <div
                className="relative min-w-0 flex-1 overflow-hidden"
                style={{ height: containerH }}>
                {items.map((story, index) => (
                  <StoryCard
                    key={story._id ?? story._key ?? index}
                    story={story}
                    index={index}
                    total={count}
                    progress={smoothProgress}
                    positionsRef={positionsRef}
                    measureRef={measureRefs[index]}
                    language={language}
                  />
                ))}
              </div>
            </div>
          </div>

          <GradualBlur
            mode="fade"
            position="bottom"
            height="24rem"
            zIndex={50}
            style={{
              background: "linear-gradient(180deg, rgba(0,27,20,0) 0%, #001b14 59.091%)",
            }}
          />
        </div>
      </section>
    </div>
  );
}

// ─── Mobile stories: one card at a time, prev/next arrows + progress bar ─────

function MobileTradersStories({
  data,
  items,
  language,
}: {
  data: TradersStoriesData;
  items: ITraderStory[];
  language?: string;
}) {
  const count = items.length;
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, skipSnaps: false });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(count > 1);

  useEffect(() => {
    if (!emblaApi) return;
    const sync = () => {
      setSelected(emblaApi.selectedScrollSnap());
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    sync();
    emblaApi.on("select", sync).on("reInit", sync);
    return () => {
      emblaApi.off("select", sync).off("reInit", sync);
    };
  }, [emblaApi]);

  return (
    <div className="relative w-full bg-[#001b14]">
      <div className="flex flex-col">
        <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center gap-[2rem] pt-[6rem] pb-[6rem] text-center">
          {data.chip && (
            <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-white/70 italic">{data.chip}</p>
          )}
          {data.title && (
            <div className="richtext [&_p]:hidden prose-em:font-blacker prose-em:text-[#d6eee7] prose-headings:text-[4.5rem] prose-headings:leading-[1.15] prose-headings:font-medium prose-headings:tracking-[-0.225rem] prose-headings:text-white">
              <PortableText
                value={data.title}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[3.36rem] px-[2rem] pb-[8rem]">
          {/* Carousel viewport */}
          <div
            ref={emblaRef}
            className="overflow-hidden">
            <div className="flex touch-pan-y -ml-[2rem]">
              {items.map((story) => (
                <div
                  key={story._id ?? story._key}
                  className="flex min-w-0 flex-[0_0_100%] pl-[2rem]">
                  <StoryCardMobile
                    story={story}
                    language={language}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Pager: prev · progress · next */}
          {count > 1 && (
            <div className="flex items-center gap-[1.4545rem]">
              <button
                type="button"
                aria-label="Previous story"
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canPrev}
                className="grid h-[5.818rem] w-[5.818rem] shrink-0 place-items-center rounded-[0.242rem] border-[0.139rem] border-white/[0.14] text-white transition-opacity disabled:opacity-30">
                <ChevronIcon
                  direction="left"
                  className="h-[2.181rem] w-[2.181rem]"
                />
              </button>
              <div className="relative h-[0.25rem] min-w-0 flex-1 overflow-hidden rounded-[0.242rem] bg-white/10">
                <div
                  className="absolute inset-y-0 left-0 rounded-[0.242rem] bg-[#65b495] transition-[width] duration-300"
                  style={{ width: `${((selected + 1) / count) * 100}%` }}
                />
              </div>
              <button
                type="button"
                aria-label="Next story"
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canNext}
                className="grid h-[5.818rem] w-[5.818rem] shrink-0 place-items-center rounded-[0.242rem] border-[0.139rem] border-white/[0.14] text-white transition-opacity disabled:opacity-30">
                <ChevronIcon
                  direction="right"
                  className="h-[2.181rem] w-[2.181rem]"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TradersStories({ data, language }: { data: TradersStoriesData; language?: string }) {
  const items = data?.items ?? [];
  const isMobile = useIsMobile({ breakpoint: 1023 });

  if (!items.length && !data?.title) return null;

  if (isMobile) {
    return (
      <MobileTradersStories
        data={data}
        items={items}
        language={language}
      />
    );
  }

  return (
    <DesktopTradersStories
      data={data}
      items={items}
      language={language}
    />
  );
}

// ─── Live payouts ────────────────────────────────────────────────────────────

const PayoutCard = memo(function PayoutCard({ item }: { item: PageHomeLivePayoutItem }) {
  return (
    <article className="relative flex h-[11.5rem] w-[39.75rem] shrink-0 items-center overflow-hidden rounded-[0.25rem] border-l-2 border-[#65b495] bg-[#f4f5f5]/10">
      {/* Halftone texture — Figma "image 418" (the frame is drawn at 1.322x, values below are scaled back):
          the portrait image rotated -90° fills a 447x211 box at -64/-59, difference blend @ 6% */}
      <div className="pointer-events-none absolute left-[-8rem] top-[-7.375rem] h-[26.375rem] w-[55.875rem] opacity-[0.06] mix-blend-difference">
        <Image
          src="/text-only-crypto/dots.png"
          alt=""
          width={736}
          height={1308}
          className="absolute left-1/2 top-1/2 h-[55.875rem] w-[26.375rem] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 object-cover"
        />
      </div>

      {/* IQ logo */}
      <div className="absolute left-[3.375rem] top-1/2 -translate-y-1/2">
        <IQLogo />
      </div>

      {/* Vertical divider */}
      <div className="absolute left-[9.75rem] top-1/2 h-[6.5rem] w-px -translate-y-1/2 bg-white/10" />

      {/* Text content */}
      <div className="absolute left-[13rem] top-[2.5rem] flex flex-col gap-[0.875rem]">
        <div className="flex items-center gap-[1.25rem] whitespace-nowrap">
          {item.name && (
            <p className="mb-0 font-geist text-[1.75rem] font-medium leading-[1.2] tracking-[-0.07rem] text-white/60">
              {item.name}
            </p>
          )}
          {item.amount && (
            <p className="mb-0 font-geist text-[2.5rem] font-semibold leading-[1.2] tracking-[-0.1rem] text-white">
              {item.amount}
            </p>
          )}
        </div>
        {item.country && (
          <div className="inline-flex w-fit items-center bg-[#65b495]/35 px-[0.875rem] py-[0.5rem]">
            <p className="mb-0 whitespace-nowrap font-geist-mono text-[1.5rem] font-medium uppercase leading-[1.2] tracking-[-0.06rem] text-white">
              {item.country}
            </p>
          </div>
        )}
      </div>
    </article>
  );
});

function MarqueeRow({
  items,
  direction,
  scrollVelocity,
}: {
  items: PageHomeLivePayoutItem[];
  direction: 1 | -1;
  scrollVelocity: MotionValue<number>;
}) {
  const x = useMotionValue(0);
  const firstCopyRef = useRef<HTMLDivElement>(null);
  const singleWidthRef = useRef(0);
  const initialized = useRef(false);
  const isPaused = useRef(false);

  const repeated = useMemo(() => Array.from({ length: REPEATS }, () => items), [items]);

  useEffect(() => {
    const update = () => {
      if (firstCopyRef.current) {
        singleWidthRef.current = firstCopyRef.current.offsetWidth + GAP_PX;
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (firstCopyRef.current) ro.observe(firstCopyRef.current);
    return () => ro.disconnect();
  }, []);

  useAnimationFrame((_, delta) => {
    if (isPaused.current) return;
    const singleWidth = singleWidthRef.current;
    if (singleWidth <= GAP_PX) return;

    if (!initialized.current) {
      initialized.current = true;
      if (direction === 1) {
        x.set(-singleWidth);
        return;
      }
    }

    const vel = scrollVelocity.get();
    const boost = Math.min(Math.abs(vel) * 0.003, 6);
    const speed = BASE_PX_PER_FRAME * (1 + boost) * (delta / 16.67);

    if (direction === -1) {
      let newX = x.get() - speed;
      if (newX < -singleWidth) newX += singleWidth;
      x.set(newX);
    } else {
      let newX = x.get() + speed;
      if (newX > 0) newX -= singleWidth;
      x.set(newX);
    }
  });

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        maskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
      }}
      onMouseEnter={() => {
        isPaused.current = true;
      }}
      onMouseLeave={() => {
        isPaused.current = false;
      }}>
      <motion.div
        style={{ x }}
        className="flex gap-[3rem] will-change-transform">
        {repeated.map((copyItems, r) => (
          <div
            key={r}
            ref={r === 0 ? firstCopyRef : undefined}
            className="flex shrink-0 gap-[3rem]">
            {copyItems.map((item, i) => (
              <PayoutCard
                key={`${r}-${i}-${item._key ?? ""}`}
                item={item}
              />
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function PayoutsFooterBar({ data }: { data: PageHomeSchema }) {
  if (!data.livePayoutsButton && !data.livePayoutsPartnerText) return null;
  return (
    <div className="w-full bg-white/[0.03]">
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center justify-between gap-[3rem] py-[6.875rem] lg:w-[min(140rem,calc(100%-8rem))] lg:flex-row">
        <div className="flex shrink-0 items-center justify-between gap-[1.875rem]">
          {data.livePayoutsPartnerText && (
            <p className="mb-0 font-geist text-[1.75rem] leading-[1.5] text-white/70 text-center lg:max-w-[40rem] lg:text-left">
              {data.livePayoutsPartnerText}
            </p>
          )}
        </div>
        {data.livePayoutsButton && (
          <div className="w-full shrink-0 lg:w-fit">
            <CTAButton data={data.livePayoutsButton} />
          </div>
        )}
      </div>
    </div>
  );
}

function LivePayouts({ data }: { data: PageHomeSchema }) {
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);

  const items = data.livePayoutsItems ?? [];
  if (!data.livePayoutsTitle && !items.length) return null;

  return (
    <section className="relative w-full overflow-x-clip bg-[#001b14] lg:pt-0 pt-[10rem]">
      {/* Header */}
      <div className="mx-auto mb-[8rem] flex w-[min(90rem,calc(100%-4rem))] flex-col items-center gap-[3rem] px-[2rem] text-center lg:px-0">
        {data.livePayoutsChip && (
          <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-white/70">{data.livePayoutsChip}</p>
        )}
        {data.livePayoutsTitle && (
          <div className="richtext prose-headings:text-white [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance]">
            <PortableText
              value={data.livePayoutsTitle}
              components={richTextComponent}
            />
          </div>
        )}
        {data.livePayoutsButton && <CTAButton data={data.livePayoutsButton} />}
      </div>

      {/* 3 marquee rows — each row gets a unique slice of items so no name repeats across rows */}
      {items.length > 0 &&
        (() => {
          const chunk = Math.ceil(items.length / 3);
          const row1 = items.slice(0, chunk);
          const row2 = items.slice(chunk, chunk * 2);
          const row3 = items.slice(chunk * 2);
          return (
            <div className="flex flex-col gap-[3rem] pb-[8rem] lg:pb-[16rem]">
              <MarqueeRow
                items={row1}
                direction={-1}
                scrollVelocity={scrollVelocity}
              />
              <MarqueeRow
                items={row2}
                direction={1}
                scrollVelocity={scrollVelocity}
              />
              <MarqueeRow
                items={row3}
                direction={-1}
                scrollVelocity={scrollVelocity}
              />
            </div>
          );
        })()}

      <PayoutsFooterBar data={data} />
    </section>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

export default function HomeStoriesPayoutsSection({ data, language }: { data: PageHomeSchema; language?: string }) {
  return (
    <>
      <TradersStories
        language={language}
        data={{
          chip: data.tradersStoriesChip,
          title: data.tradersStoriesTitle,
          items: data.tradersStoriesItemsRefs ?? data.tradersStoriesItems,
        }}
      />
      <LivePayouts data={data} />
    </>
  );
}
